import {
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';

import type { AppDispatch, ThunkApiType } from '@/store';

import { firebaseConfig, vapidKey } from '@/constants/env';
import { uiSlice } from '@/domain/ui/redux';
import { userSlice } from '@/domain/user/redux';
import { service } from '@/share';
import { resetStore } from '@/store';

const endPushSubscription = async () => {
  const user = await service.user.getUserInfo();
  const { username } = user;
  // ServiceWorker Subscription
  if ('Notification' in window && 'serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(async (registration) => {
      if (registration.active && Notification.permission === 'granted') {
        // FCM設定
        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);
        const token = await getToken(messaging, {
          serviceWorkerRegistration: registration, vapidKey,
        });
        if (token) {
          await service.device.deleteDevice({
            parentId: username,
            createDatetime: '',
            token,
          });
        }
      }
    });
  }
};

const signOutCore = async (dispatch: AppDispatch) => {
  await endPushSubscription();
  await service.user.signOut();
  dispatch(resetStore());
};

/**
 * ログアウト
 */
export const signOut = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'user/signOut',
  async (_, thunkApi) => {
    await signOutCore(thunkApi.dispatch);
    thunkApi.dispatch(userSlice.actions.setMode('signin'));
    thunkApi.dispatch(userSlice.actions.setUser(undefined));
  },
);

export const changeEmail = createAsyncThunk<
  void,
  string,
  ThunkApiType
>(
  'auth/changeEmail',
  async (email, thunkApi) => {
    const { status, msg } = await service.user.changeEmail(email);
    if (status === 'ok') {
      // ログアウトする
      await signOutCore(thunkApi.dispatch);
      thunkApi.dispatch(userSlice.actions.setMode('signin'));
      thunkApi.dispatch(userSlice.actions.setUser(undefined));
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: msg }));
    } else {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: msg }));
    }
  },
);

export const resendChangeEmail = createAsyncThunk<void, void, ThunkApiType>(
  'auth/resendChangeEmail',
  async (_, thunkApi) => {
    const { status, msg } = await service.user.resendChangeEmail();
    if (status === 'ok') {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: msg }));
    } else {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: msg }));
    }
  },
);

export const verifyEmail = createAsyncThunk<
  void,
  string,
  ThunkApiType
>(
  'auth/verifyEmail',
  async (code, thunkApi) => {
    const email = thunkApi.getState().user.user?.attributes?.email || '';
    const items = await service.child.getChildList();
    const facilityList = await service.childApply.getFacilityList();
    const { status, msg } = await service.user.verifyEmail(code, email);
    if (status === 'ok') {
      // 子どもの施設に報告する
      const promises = [];
      for (let i = 0; i < items.length; i += 1) {
        const childFacilityList = facilityList.filter(
          (v) => v.data.childId === items[i].childId.value,
        );
        for (let j = 0; j < childFacilityList.length; j += 1) {
          promises.push(service.user.reportUpdateChild(
            childFacilityList[j].data.facilityId,
            items[i].childId.value,
            email,
          ));
        }
      }
      await Promise.all(promises);
      // ログアウトする
      await signOutCore(thunkApi.dispatch);
      thunkApi.dispatch(userSlice.actions.setMode('signin'));
      thunkApi.dispatch(userSlice.actions.setUser(undefined));
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: msg }));
    } else {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: msg }));
    }
  },
);
